import { lazy, Suspense, type ReactElement } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { FournisseurSession, useSession } from './coquille/ContexteSession.js';
import { Coquille } from './coquille/Coquille.js';
import { Connexion } from './ecrans/Connexion.js';
import { MesEspaces } from './ecrans/MesEspaces.js';
import { Plateforme } from './ecrans/Plateforme.js';
import { ClientPlateforme } from './ecrans/ClientPlateforme.js';
import { NouveauClient } from './ecrans/NouveauClient.js';
import { AccueilEspace } from './ecrans/AccueilEspace.js';
import { Automatisations } from './ecrans/Automatisations.js';
import { Versions } from './ecrans/Versions.js';
import { Projets } from './ecrans/Projets.js';
import { Projet } from './ecrans/Projet.js';
import { NouveauProjet } from './ecrans/NouveauProjet.js';
import { NouvelleAutomatisation } from './ecrans/NouvelleAutomatisation.js';
import { Environnements } from './ecrans/Environnements.js';
import ConfigurationEnvironnement from './ecrans/ConfigurationEnvironnement.js';
import { Executions } from './ecrans/Executions.js';
import { Parametres } from './ecrans/Parametres.js';
import { CHEMINS } from './routes.js';

/**
 * L EDITEUR EST CHARGE A LA DEMANDE.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ 60 Ko DE MOTEUR GRAPHIQUE NE PARTENT QU A L OUVERTURE D UN GRAPHE.    ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * Quelqu un qui consulte sa liste, ses executions ou ses validations n a
 * aucune raison de telecharger @xyflow/react. `lazy()` en fait un paquet
 * SEPARE, que le navigateur ne demande qu au moment d en avoir besoin.
 *
 * IMPORT DYNAMIQUE, ET C EST LUI QUI DECOUPE. Un import statique --
 * `import { EditeurAutomatisation } from ...` -- ramenerait tout dans le
 * paquet principal, et rien ne le signalerait avant la prochaine mesure.
 * Une preuve permanente verifie qu aucun autre fichier n importe la
 * bibliotheque.
 */
const EditeurAutomatisation = lazy(async () => {
  const m = await import('./ecrans/EditeurAutomatisation.js');
  return { default: m.EditeurAutomatisation };
});

// MEME REGIME QUE L EDITEUR : la vue lecture seule monte le canevas, donc
// elle porte xyflow. Un import statique la ramenerait dans le paquet
// principal et le bloc 8 crierait -- a juste titre.
const BrouillonLectureSeule = lazy(async () => {
  const m = await import('./ecrans/BrouillonLectureSeule.js');
  return { default: m.BrouillonLectureSeule };
});


/**
 * L'APPLICATION — session, routage, et rien d'autre.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ LE FRONT AFFICHE ET DEMANDE. IL NE DECIDE RIEN DU METIER.             ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * ── QUATRE ETATS, ET UN SEUL A L'ECRAN ──────────────────────────────────
 *
 *   chargement     on ne sait pas encore -- rien de definitif ne s'affiche
 *   indisponible   l'API ne repond pas : on le DIT
 *   anonyme        aucune session : l'ecran de connexion
 *   connecte       le routage prend la main
 *
 * Le second existe parce que confondre « pas connecte » et « service en panne »
 * ferait saisir des identifiants dans le vide.
 *
 * ── `BrowserRouter` SUPPOSE LE REPLI NGINX ──────────────────────────────
 *
 * /espaces/acme/automatisations n'existe sur aucun disque : nginx rend
 * index.html pour tout chemin inconnu, et le routeur lit ensuite l'URL. Sans
 * ce repli (etape 19), un rechargement de page donnerait 404.
 */
export function Application(): ReactElement {
  return (
    <FournisseurSession>
      <BrowserRouter>
        <Aiguillage />
      </BrowserRouter>
    </FournisseurSession>
  );
}

function Aiguillage(): ReactElement {
  const { etat } = useSession();

  if (etat.phase === 'chargement') {
    return <div className="plein-centre"><p className="discret">Chargement…</p></div>;
  }
  if (etat.phase === 'indisponible') return <Indisponible message={etat.message} />;

  if (etat.phase === 'anonyme') {
    // AUCUNE ROUTE PRIVEE N'EXISTE tant que personne n'est connecte : un
    // chemin d'espace ramene a la connexion, sans page cassee entre-temps.
    return (
      <Routes>
        <Route path={CHEMINS.connexion} element={<Connexion />} />
        <Route path="*" element={<Navigate to={CHEMINS.connexion} replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Deja connecte : la page de connexion n'a plus d'objet. */}
      <Route path={CHEMINS.connexion}
             element={<Navigate to={CHEMINS.espaces} replace />} />
      <Route path={CHEMINS.espaces} element={<MesEspaces />} />
      {/* MONDE PLATEFORME [28-BU], hors de la coquille de locataire :
          ces ecrans n ont pas d espace courant. */}
      <Route path={CHEMINS.plateforme} element={<Plateforme />} />
      <Route path={CHEMINS.nouveauClient} element={<NouveauClient />} />
      <Route path="/plateforme/clients/:idClient" element={<ClientPlateforme />} />
      {/* HORS DE LA COQUILLE : un canevas prend toute la largeur, et la
          barre laterale d espace ne sert a rien quand on dessine. */}
      <Route path="/espaces/:code/projets/:projet/automatisations/:id/editeur"
             element={
               <Suspense fallback={<ChargementEditeur />}>
                 <EditeurAutomatisation />
               </Suspense>
             } />
      <Route path="/espaces/:code/projets/:projet/automatisations/:id/brouillon/lecture"
             element={
               <Suspense fallback={<ChargementLecture />}>
                 <BrouillonLectureSeule />
               </Suspense>
             } />
      <Route path="/espaces/:code" element={<Coquille />}>
        <Route index element={<AccueilEspace />} />
        <Route path="projets" element={<Projets />} />
        {/* AVANT `projets/:projet` : sinon `nouveau` serait lu comme un
            code de projet et l ecran de creation deviendrait inatteignable. */}
        <Route path="projets/nouveau" element={<NouveauProjet />} />
        <Route path="projets/:projet" element={<Projet />}>
          <Route index element={<Navigate to="automatisations" replace />} />
          {/* AVANT `automatisations/:id/...` et frere de la liste : un
              segment litteral se declare avant tout segment parametre. */}
          <Route path="automatisations/nouvelle"
                 element={<NouvelleAutomatisation />} />
          {/* 29-G ph.3 — DANS la coquille : une liste n a pas besoin de la
              pleine page, et la navigation du projet reste accessible. Le
              segment :id est PARAMETRE, donc apres le litteral `nouvelle`. */}
          <Route path="automatisations/:id/versions"
                 element={<Versions />} />
          <Route path="automatisations" element={<Automatisations />} />
          {/* RUBRIQUE ACTIVE, la premiere depuis Automatisations. Le chemin
              reste une chaine litterale sans rapport typé avec CHEMINS
              [dette 44] : non aggravee, non levee. */}
          <Route path="environnements" element={<Environnements />} />
          <Route path="environnements/:nature/configuration"
                 element={<ConfigurationEnvironnement />} />
          {/* 29-F : deux rubriques ouvertes, chacune servie par une route
              moteur DEDIEE. Chemins litteraux fixes, donc sans conflit
              d ordre avec les freres parametres. */}
          <Route path="executions" element={<Executions />} />
          <Route path="parametres" element={<Parametres />} />
        </Route>
        {/* Les autres rubriques viendront ici -- la coquille ne bouge pas. */}
      </Route>
      <Route path="*" element={<Navigate to={CHEMINS.espaces} replace />} />
    </Routes>
  );
}

function Indisponible({ message }: { message: string }): ReactElement {
  return (
    <div className="plein-centre">
      <h1>Anelya est momentanément injoignable</h1>
      <p className="discret">{message}</p>
      <button type="button" className="bouton bouton--principal"
              onClick={() => { window.location.reload(); }}>
        Réessayer
      </button>
    </div>
  );
}

/**
 * L ATTENTE PENDANT LE TELECHARGEMENT DU PAQUET.
 *
 * Elle est REELLE : 60 Ko arrivent, et sur une connexion lente cela se
 * voit. Un « Chargement... » pose au hasard laisserait croire a une page
 * cassee ; on DIT ce qui se passe, et l on garde la meme structure que
 * l ecran a venir pour que rien ne saute a l arrivee.
 */
/** DUPLIQUE PLUTOT QUE MUTUALISE : le module lazy n est pas encore la, et
 *  dire « editeur » quand on ouvre une lecture seule serait faux. */
function ChargementLecture(): ReactElement {
  return (
    <div className="editeur-plein">
      <header className="editeur-entete">
        <span className="editeur-entete__titre">Automatisation</span>
      </header>
      <div className="editeur-canevas plein-centre">
        <p className="discret">Ouverture de la vue en lecture seule…</p>
      </div>
    </div>
  );
}

function ChargementEditeur(): ReactElement {
  return (
    <div className="editeur-plein">
      <header className="editeur-entete">
        <span className="editeur-entete__titre">Éditeur</span>
      </header>
      <div className="editeur-canevas plein-centre">
        <p className="discret">Préparation de l’éditeur…</p>
      </div>
    </div>
  );
}
